import React from "react";
import {
  FaInfoCircle,
  FaUserShield,
  FaCookie,
  FaShareAlt,
  FaShieldAlt,
  FaLink,
  FaUserCheck,
  FaExclamationTriangle,
  FaEnvelope,
} from "react-icons/fa";

const PrivacyPolicy = () => {
  return (
    <div className="w-full bg-white dark:bg-[#0f172a] text-gray-800 dark:text-gray-200 transition-colors duration-300">
      <section className="max-w-5xl mx-auto px-6 sm:px-10 py-12 space-y-10 text-left">
        {/* Introduction */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaInfoCircle className="mr-2 text-primary dark:text-[#60a5fa]" /> Introduction
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Welcome to www.mr-reel.com ("Website"). We value your privacy and are committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, and protect your data when you visit or interact with our Website. By using our services, you consent to the practices described in this policy.
          </p>
          <p className="text-gray-700 dark:text-gray-300">
            Our role is to provide insights and recommendations regarding online gaming, but all financial transactions, account management, and security measures are handled solely by the third-party operators we feature. We urge users to review the responsible gaming policies of any third-party sites they choose to engage with.
          </p>
        </div>

        {/* Information We Collect */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaUserShield className="mr-2 text-primary dark:text-[#60a5fa]" /> Information We Collect
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            We collect information to improve our services and enhance your experience. The types of information we collect include:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-gray-700 dark:text-gray-300">
            <li><strong>Personal Information:</strong> When you sign up, contact us, or use certain features of our Website, we may collect your name, email address, phone number, and other details you provide voluntarily.</li>
            <li><strong>Usage Data:</strong> We automatically collect information such as your IP address, browser type, device information, referring website, pages visited, time spent on our site, and interactions with content.</li>
            <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar technologies to enhance user experience, track site performance, and provide relevant content. You can manage cookie preferences through your browser settings.</li>
          </ul>
        </div>

        {/* How We Use Your Information */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaCookie className="mr-2 text-primary dark:text-[#60a5fa]" /> How We Use Your Information
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            We use the information we collect for various purposes, including:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-gray-700 dark:text-gray-300">
            <li><strong>Enhancing User Experience:</strong> Personalizing content, optimizing website functionality, and improving navigation.</li>
            <li><strong>Communication:</strong> Responding to inquiries, sending updates, and providing relevant information about our services.</li>
            <li><strong>Marketing & Analytics:</strong> Analyzing website traffic, measuring engagement, and tailoring promotions based on user interactions.</li>
            <li><strong>Security & Compliance:</strong> Preventing fraud, maintaining security, and complying with legal obligations.</li>
          </ul>
        </div>

        {/* Sharing Your Information */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaShareAlt className="mr-2 text-primary dark:text-[#60a5fa]" /> Sharing Your Information
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Your privacy is important to us, and we do not sell or rent your personal data. However, we may share information in the following situations:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-gray-700 dark:text-gray-300">
            <li><strong>With Trusted Service Providers:</strong> We work with third-party service providers who assist in website hosting, analytics, customer support, and other essential operations. These providers are contractually obligated to protect your data.</li>
            <li><strong>Legal Compliance:</strong> We may disclose information when required by law, in response to legal requests, or to protect our rights and the safety of our users.</li>
            <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction.</li>
          </ul>
        </div>

        {/* Cookies and Tracking Technologies */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaCookie className="mr-2 text-primary dark:text-[#60a5fa]" /> Cookies and Tracking Technologies
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Cookies help us improve your experience by remembering preferences and understanding user behavior. We use:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-gray-700 dark:text-gray-300">
            <li><strong>Essential Cookies:</strong> Necessary for website functionality.</li>
            <li><strong>Performance Cookies:</strong> Help us analyze site traffic and user interactions.</li>
            <li><strong>Marketing Cookies:</strong> Enable personalized advertising and promotional campaigns.</li>
          </ul>
          <p className="mt-3 text-gray-700 dark:text-gray-300">
            You can adjust your cookie settings in your browser or opt out of certain tracking technologies.
          </p>
        </div>

        {/* Data Security */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaShieldAlt className="mr-2 text-primary dark:text-[#60a5fa]" /> Data Security
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            We take appropriate security measures to safeguard your personal information against unauthorized access, alteration, or misuse. These include encryption, access controls, and secure data storage. While we strive to protect your data, no system is entirely foolproof, and we encourage users to take precautions when sharing information online.
          </p>
        </div>

        {/* Third-Party Links */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaLink className="mr-2 text-primary dark:text-[#60a5fa]" /> Third-Party Links
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            Our Website may contain links to third-party websites. Please note that we are not responsible for the privacy practices or content of external sites. We encourage you to review their policies before providing personal information.
          </p>
        </div>

        {/* Your Rights and Choices */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaUserCheck className="mr-2 text-primary dark:text-[#60a5fa]" /> Your Rights and Choices
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            Depending on your location, you may have certain rights regarding your personal information, including:
          </p>
          <ul className="list-disc ml-6 space-y-2 text-gray-700 dark:text-gray-300">
            <li><strong>Access & Correction:</strong> You can request access to the data we hold about you and correct inaccuracies.</li>
            <li><strong>Deletion:</strong> You may request the deletion of your personal data, subject to legal and operational considerations.</li>
            <li><strong>Opt-Out Options:</strong> You can unsubscribe from marketing communications or adjust cookie preferences at any time.</li>
          </ul>
          <p className="mt-3 text-gray-700 dark:text-gray-300">
            To exercise these rights, please contact us using the details below.
          </p>
        </div>

        {/* Limitation of Liability */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaExclamationTriangle className="mr-2 text-primary dark:text-[#fbbf24]" /> Limitation of Liability
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            The content on www.mr-reel.com is intended for general informational purposes only. While we make every effort to ensure the accuracy and relevance of the information provided, we do not guarantee that all content is up-to-date or error-free. We are not responsible for any inaccuracies, omissions, or reliance placed upon the information by users of this Website.
          </p>
        </div>

        {/* Disclaimer */}
        <div>
          <h2 className="text-xl font-bold mb-3">Disclaimer</h2>
          <p className="text-gray-700 dark:text-gray-300">
            Under no circumstances shall www.mr-reel.com or its team be liable for any financial loss, damages, or consequences arising from the use of the information contained on this Website. Visitors are responsible for verifying the legality and taxation of online gaming in their respective jurisdictions before engaging with any third-party platforms. Additionally, users should always review and agree to the terms and conditions of any online casino or gaming site before registering or making any transactions.
          </p>
        </div>

        {/* Changes to This Privacy Policy */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaExclamationTriangle className="mr-2 text-primary dark:text-[#fbbf24]" /> Changes to This Privacy Policy
          </h2>
          <p className="text-gray-700 dark:text-gray-300">
            We may update this Privacy Policy from time to time to reflect changes in our practices, technology, or legal requirements. Any updates will be posted on this page with the revised date. Continued use of our Website after changes take effect constitutes acceptance of the updated policy.
          </p>
        </div>

        {/* Contact Us */}
        <div>
          <h2 className="flex items-center text-xl font-bold mb-3">
            <FaEnvelope className="mr-2 text-primary dark:text-[#60a5fa]" /> Contact Us
          </h2>
          <p className="mb-3 text-gray-700 dark:text-gray-300">
            If you have any questions about this Privacy Policy, your personal information, or your rights, please reach out to us at:
          </p>
          <ul className="list-disc ml-6 space-y-1 text-gray-700 dark:text-gray-300">
            <li><strong>Email:</strong> play@mr-reel.com</li>
            <li><strong>Website:</strong> www.mr-reel.com</li>
          </ul>
          <p className="mt-3 text-gray-700 dark:text-gray-300">
            We appreciate your trust and are committed to safeguarding your privacy.
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;
